/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable no-empty */
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useTheme } from 'styled-components';
import { usePrivateLayout } from '~/layouts/private/providers/privateLayout';

import { hexToRgba } from '~/common/utils/hex-to-rgba';
import customAxios from '~/common/services/customAxios';
import { useToast } from '~/common/providers/toast';
import { useAuth } from '~/common/providers/auth';
import { SPACING } from '~/common/layout-propertis/spacing';
import { IAugustoPost } from '~/common/interfaces/IAugustoPost';
import { UserSubscriptionTypes } from '~/common/enums/UserSubscriptionTypes';
import { vipUsers } from '~/common/constants/vip-users';
import { allowNewSubscriptions } from '~/common/utils/allow-new-subscriptions';
import { Typography } from '~/common/components/Typography';
import { Modal } from '~/common/components/Modal';
import { IconButton } from '~/common/components/IconButton';
import { AlertInfo } from '~/common/components/AlertInfo';

import { PostsList } from './styles';
import { AugustoPost } from '../Post';
import { AugustoPostForm } from '../Form';

type TDynamicAugustoPostsParams = {
  posts?: IAugustoPost[];
  automaticLoad?: boolean;
  reload?: () => void;
  updatePost?: (postId: number, post: IAugustoPost) => void;
  filterPost?: (postId: number) => void;
};

const useDynamicAugustoPostsParams = (
  params?: TDynamicAugustoPostsParams
): TDynamicAugustoPostsParams => {
  const [augustoPosts, setAugustoPosts] = useState<IAugustoPost[]>([]);
  const {
    user: { subscription: userType },
  } = useAuth();

  const handleLoadPosts = useCallback(async () => {
    try {
      if (!userType || !vipUsers.includes(userType)) {
        return;
      }

      const startFromId = augustoPosts.length
        ? Math.max(...augustoPosts.map((item) => Number(item.id)))
        : null;

      const { data: response } = await customAxios.get<IAugustoPost[]>(
        '/Backes/posts',
        {
          params: {
            startFromId: Number(startFromId),
          },
        }
      );

      if ((response || []).length) {
        setAugustoPosts((prev) => [
          ...response.filter((where) => !prev.some((p) => p.id === where.id)),
          ...prev,
        ]);
      }
    } catch {}
  }, [augustoPosts, userType]);

  const updatePost = useCallback((postId: number, post: IAugustoPost) => {
    setAugustoPosts((prev) => prev.map((p) => (p.id === postId ? post : p)));
  }, []);

  const filterPost = useCallback((postId: number) => {
    setAugustoPosts((prev) => prev.filter((p) => p.id !== postId));
  }, []);

  useEffect(() => {
    if (params.automaticLoad) {
      handleLoadPosts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.automaticLoad]);

  if (!params.automaticLoad) {
    return params;
  }

  return {
    posts: augustoPosts,
    reload: handleLoadPosts,
    updatePost,
    filterPost,
  };
};

interface IAugustoInsightsModalProps {
  open: boolean;
  posts?: IAugustoPost[];
  reload?: () => void;
  close: () => void;
  updatePost?: (postId: number, post: IAugustoPost) => void;
  filterPost?: (postId: number) => void;
}
export const AugustoInsightsModal: FC<IAugustoInsightsModalProps> = ({
  open,
  posts: propsPosts = [],
  close,
  reload: propsReload,
  updatePost: propsUpdatePost,
  filterPost: propsFilterPost,
}) => {
  const { isMobile } = usePrivateLayout();
  const { showToast } = useToast();
  const [updating, setUpdating] = useState<IAugustoPost>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const theme = useTheme();
  const canShowSubscriptionCTA = allowNewSubscriptions();
  const {
    user: { subscription: userType },
  } = useAuth();

  const { posts, filterPost, reload, updatePost } =
    useDynamicAugustoPostsParams({
      posts: propsPosts,
      automaticLoad: !propsReload,
      reload: propsReload,
      updatePost: propsUpdatePost,
      filterPost: propsFilterPost,
    });

  useEffect(() => {
    if (open) {
      const postIds = posts.map((post) => post.id);
      customAxios
        .post('/Backes/posts/mark-as-read', {
          postIds,
        })
        .catch(() => {});
    }
  }, [open, posts]);

  const handleReturnFromForm = useCallback(() => {
    setCreating(false);
    setUpdating(null);
    reload();
  }, [reload]);

  const handleEditPost = useCallback((post: IAugustoPost) => {
    setUpdating(post);
  }, []);

  const handleDeletePost = useCallback(
    async (postId: number) => {
      try {
        await customAxios.delete(`/Backes/posts/${postId}`);
        filterPost(postId);
        reload();
      } catch {
        showToast({
          type: 'error',
          description: 'Houve um erro ao apagar o post.',
        });
      }
    },
    [filterPost, reload, showToast]
  );

  const renderAddButton = useMemo(
    () => userType === UserSubscriptionTypes.admin,
    [userType]
  );

  return (
    <>
      <Modal
        id="augusto-posts-modal"
        isOpen={open}
        onClose={close}
        height={isMobile ? '600px' : 'auto'}
        width="600px"
        customHeader={
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: SPACING._8,
            }}
          >
            <Typography.Label size="medium">
              Postagens do Augusto
            </Typography.Label>
            {renderAddButton && !creating && (
              <IconButton
                icon="add-line"
                backgroundColor={hexToRgba(theme.green[500], 0.25)}
                borderColor={theme.transparent}
                textOrIconColor={theme.green[500]}
                onClick={() => setCreating(true)}
              />
            )}
          </div>
        }
      >
        {creating || updating ? (
          <AugustoPostForm
            onReturn={handleReturnFromForm}
            updatePost={updatePost}
            updating={updating}
          />
        ) : (
          <>
            {vipUsers.includes(userType) ? (
              <PostsList>
                {posts.map((post) => (
                  <AugustoPost
                    post={post}
                    key={String(post.id)}
                    edit={handleEditPost}
                    remove={handleDeletePost}
                  />
                ))}
              </PostsList>
            ) : (
              <AlertInfo
                color="neutral.500"
                title="Este recurso é exclusivo do plano Platinum."
                info={
                  canShowSubscriptionCTA
                    ? 'Com o plano Platinum, você desbloqueia as ferramentas mais avançadas do sistema e entra em um novo nível de experiência no mercado cripto. Assine já!'
                    : 'No momento, novas assinaturas estão indisponíveis e este recurso segue restrito a usuários com plano Platinum ativo.'
                }
              />
            )}
          </>
        )}
      </Modal>
    </>
  );
};
